import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Stack } from '@figma/fpl-components';
import * as stylex from '@stylexjs/stylex';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToolPreviewView } from '@/components/Tool/Preview/ToolPreviewView';
import { PublishedToolCommunityBanner } from '@/components/Recipe/FlowComponents/PublishedToolCommunityBanner';
import { NoAccessError } from '@/components/Common/Error/NoAccessError';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { ErrorPage } from '@/components/Errors/ErrorPage/ErrorPage';
import { I18N_KEYS } from '@/language/keys';
import { FlowMode } from '@/enums/flow-modes.enum';
import { FF_WORKFLOW_APP_BUILDER_MODE } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { TOOL_PREVIEW_EVENT_NAMES } from '@/consts/analytics.consts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useToolByWorkflowQuery, useToolViewQuery } from '@/queries/useToolView';
import { apiPath } from '@/services/apiPath';
import { safeAxios } from '@/services/safeAxios';
import { useWorkflowStore } from '@/state/workflow.state';
import { parseVersionParam } from '@/utils/recipes';
import type { ToolRunData } from '@/api/toolRunData.api';
import type { Recipe } from '@/types/api/recipe';
import type { User } from '@/types/auth.types';

export function ToolPreviewPage({ user }: { user: User | null }) {
  const { workflowId } = useParams();
  const [searchParams] = useSearchParams();
  const version = parseVersionParam(searchParams.get('version'));
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const { value: isAppBuilderEnabled } = useFeatureFlag(FF_WORKFLOW_APP_BUILDER_MODE);
  const openedPreviewRef = useRef<string | null>(null);
  const { data: publishedTool } = useToolViewQuery(workflowId, isAppBuilderEnabled === true);
  const preview = useQuery({
    queryKey: ['tool-preview', workflowId, version, user?.uid],
    queryFn: async () => {
      const [workflow, toolConfigs] = await Promise.all([
        // fetch the workflow recipe
        safeAxios.get<Recipe>(apiPath`/v1/recipes/${workflowId}`, {
          params: { version },
        }),
        // fetch tool configuration for the workflow
        safeAxios.get<Omit<ToolRunData, 'workflowId'>>(apiPath`/v1/recipes/${workflowId}/inspect`, {
          params: { version },
        }),
      ]);
      const recipe = workflow.data;
      useWorkflowStore.getState().mergeResolvedAssets({
        ...recipe.resolvedAssets,
        ...toolConfigs.data.resolvedAssets,
      });
      return {
        recipe,
        runData: {
          ...toolConfigs.data,
          workflowId: recipe.id,
        },
      };
    },
    enabled: !!workflowId && isAppBuilderEnabled === true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // The tool's creator gets the same published banner as on /tool; it self-gates on publish state.
  const isWorkflowOwner = !!user && preview.data?.recipe.owner === user.uid;
  const { data: toolByWorkflow } = useToolByWorkflowQuery(workflowId, isWorkflowOwner);
  const { data: tool } = useToolViewQuery(toolByWorkflow?.toolId ?? undefined);

  useEffect(() => {
    if (!isAppBuilderEnabled || !preview.data) return;
    const { recipe, runData } = preview.data;
    const previewKey = `${recipe.id}:${runData.recipeVersion}`;
    if (openedPreviewRef.current === previewKey) return;
    openedPreviewRef.current = previewKey;
    track(
      TOOL_PREVIEW_EVENT_NAMES.OPENED,
      { source: 'preview', workflow_id: recipe.id, version: runData.recipeVersion },
      TrackTypeEnum.BI,
    );
  }, [isAppBuilderEnabled, preview.data, track]);

  if (isAppBuilderEnabled === false) {
    return <ErrorPage />;
  }

  if (isAppBuilderEnabled === undefined) {
    return <PageLoader />;
  }

  if (preview.error instanceof AxiosError && preview.error.response?.status === 403) {
    return <NoAccessError />;
  }

  if (preview.isError) {
    return (
      <ErrorPage actions={[{ label: t(I18N_KEYS.ERROR_PAGE.RETRY_ACTION), onClick: () => void preview.refetch() }]} />
    );
  }

  if (!preview.data || preview.isFetching) {
    return <PageLoader />;
  }

  const { recipe, runData } = preview.data;
  const toolName = (recipe.toolConfig?.metadata?.name ?? publishedTool?.name)?.trim() || recipe.name;

  return (
    <Stack.Col {...stylex.props(styles.root)}>
      {tool?.isOwner && (
        <PublishedToolCommunityBanner toolId={tool.toolId} toolName={tool.name ?? ''} workflowId={recipe.id} />
      )}
      <Stack.Col {...stylex.props(styles.preview)}>
        <ToolPreviewView
          key={`${recipe.id}:${recipe.version}`}
          recipe={recipe}
          toolName={toolName}
          runData={runData}
          user={user}
          onBackToWorkflow={() => {
            track(
              TOOL_PREVIEW_EVENT_NAMES.BACK_TO_EDITING_CLICKED,
              { source: 'preview', workflow_id: recipe.id, version: runData.recipeVersion },
              TrackTypeEnum.BI,
            );
            const params = new URLSearchParams(searchParams);
            params.set('view', FlowMode.Workflow);
            window.location.href = `/flow/${recipe.id}?${params.toString()}`;
          }}
        />
      </Stack.Col>
    </Stack.Col>
  );
}

const styles = stylex.create({
  root: { height: '100%' },
  preview: { flex: 1, minHeight: 0 },
});
